/**
 * Dashboard documents: types, storage and the mutation surface.
 *
 * A dashboard is tabs of tiles, and **a tile is a query string** - the same
 * format `buildQueryString()` / `parsePlotState()` and `SavedView` already use.
 * That is what makes "add the chart I am looking at" and "open this tile back in
 * Explore" one-liners rather than a translation layer.
 *
 * **Context is a variable, not a boolean.** A tile either references the
 * dashboard's `ship` / `window` variables or pins a literal - see
 * `variables.ts` for why, and for what that buys later (fixed dashboards,
 * slicers, row repeats). v2 documents, which said the same thing with
 * `bindContext` plus a `context` object, are lifted on read by `migrate()`.
 *
 * **Saving is explicit.** Mutations mark the document dirty and Save writes it.
 * Against `localStorage` autosave was free; against Postgres it is a whole
 * document written ten times a second during a drag, a `version` check racing
 * itself, and a shared dashboard edited by whoever brushed a tile while reading
 * it. List-level actions - create, rename, move, delete, folders - still write
 * immediately: they are not part of an arranging session.
 *
 * Storage goes through `DashboardRepository`, and every method is async even
 * though the only implementation today is synchronous `localStorage`. That is
 * deliberate: server-side dashboards are a swap of this one object (see
 * `docs/plans/dashboards.md`), and callers should already be written for it.
 */

import {
  Atom,
  activePreset,
  endDate,
  plottedShipIds,
  startDate,
} from '../../state/store.ts';
import { stripContext, extractContext, type ViewContext } from '../../state/presets.ts';
import {
  SHIP_VAR,
  WINDOW_VAR,
  defaultDetachedVariables,
  defaultVariables,
  isRef,
  type DashboardVariable,
  type SlicerDef,
  type SlicerMode,
  type TimeWindow,
  type VariableRef,
} from './variables.ts';

/** Storage key. Versioned like `VIEWS_KEY`, so a schema break can migrate. */
export const DASHBOARDS_KEY = 'neptune_dashboards_v2';
/** v1 held a bare array of dashboards, before folders existed. */
const DASHBOARDS_KEY_V1 = 'neptune_dashboards_v1';

/**
 * Bumped when the stored shape changes in a way `migrate()` must handle.
 *
 * v3 is the shape dashboards go to Postgres in: variables instead of
 * `bindContext`, plus the fields that let rows, panel kinds and repeats arrive
 * without a migration over everybody's saved layouts (`docs/plans/dashboards.md`).
 */
export const SCHEMA_VERSION = 3;

/* ── Layout units ───────────────────────────────────────── */

/** Columns in the layout grid. 12 divides by 2, 3, 4 and 6. */
export const GRID_COLS = 12;
/** Height of one layout row, in px. Tile heights are multiples of this. */
export const ROW_HEIGHT = 30;
/** Gap between tiles, in px. Applies on both axes. */
export const GRID_GAP = 10;

export const MIN_TILE_W = 2;
export const MIN_TILE_H = 4;
/** A slicer can shrink to two grid rows: its header and one line of control. */
export const MIN_SLICER_H = 2;

/** The shortest a tile may be: slicers are allowed to be a single row. */
export function minTileH(tile: Pick<DashboardTile, 'kind'>): number {
  return tile.kind === 'slicer' ? MIN_SLICER_H : MIN_TILE_H;
}
const DEFAULT_TILE_W = 4;
const DEFAULT_TILE_H = 9;

/**
 * The source a tile reads from when it does not say.
 *
 * Historical DEC TV is the only one today and will not be the only one, so it
 * is written onto every tile rather than assumed at the point of fetch.
 */
export const DEFAULT_TILE_SOURCE = 'historical';

/**
 * The source a chart-builder query reads: its `ds=`, which Explore writes only
 * when it is not the default. A tile built while Explore is on Live is a Live
 * tile - its signals are Live's column names, and read from EOS it draws nothing.
 */
export function sourceOfQuery(query: string): string {
  return new URLSearchParams(query).get('ds') || DEFAULT_TILE_SOURCE;
}

/* ── Types ──────────────────────────────────────────────── */

/** Position and size in grid units - `x`/`w` in columns, `y`/`h` in rows. */
export interface TilePos {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * What a tile draws for, as stored.
 *
 * `"$ship"` / `"$window"` follow the dashboard's variables - the ordinary case,
 * and what every tile said implicitly under `bindContext: false`. A literal
 * pins: a ship list (anchor first) or a window, which is the old
 * `bindContext: true`.
 */
export interface TileBind {
  ship: VariableRef | string[];
  window: VariableRef | TimeWindow;
  /**
   * Context params that are neither ship nor window and still have to survive a
   * round trip - `pc` (a pinned period comparison) and `live`. Kept as the raw
   * query params they came from rather than parsed, because the only consumers
   * are the two places that put them back into a query string.
   */
  extra?: Record<string, string>;
}

/** What a tile renders: a chart, or a slicer that narrows the charts beside it. */
export type TileKind = 'chart' | 'slicer';

export interface DashboardTile {
  id: string;
  /** The row this tile belongs to, or null for an ungrouped tile - every one today. */
  rowId: string | null;
  kind: TileKind;
  /** Shown in the tile header. Seeded from the recipe, then user-editable. */
  title: string;
  /** The chart recipe, context stripped. */
  query: string;
  /** Which data source the recipe reads. Never implied - more are coming. */
  source: string;
  /** Where ship and window come from: a variable, or a literal pin. */
  bind: TileBind;
  /** Tile-level filter rows, on top of the dashboard's. Reserved. */
  filters: unknown[];
  /** Repeat this tile once per value of the named variable. Reserved. */
  repeat: string | null;
  /**
   * The catalog chart this tile is linked to, or null for a tile that carries
   * its own recipe. Grafana's library-panel split: a linked tile draws whatever
   * the chart is *now*, so an edit in the catalog reaches every dashboard that
   * uses it. `title`, `query` and `bind` then hold the recipe as it was when
   * linked - never drawn while the chart resolves, but what Detach falls back
   * to when the chart has been deleted or unshared.
   */
  chartId: string | null;
  /** True when the tab's slicers do not narrow this tile. Absent means they do. */
  ignoreSlicers?: boolean;
  /** Present on a `slicer` tile: the column it slices and how it is shown. */
  slicer?: SlicerDef;
  pos: TilePos;
}

/**
 * A group of tiles, collapsible, as in Grafana. Declared but not yet rendered -
 * holding the field from the start is what keeps rows from being a migration
 * over documents people have already saved.
 */
export interface DashboardRow {
  id: string;
  title: string;
  collapsed: boolean;
  /** Repeat this row once per value of the named variable. Reserved. */
  repeat: string | null;
}

export interface DashboardTab {
  id: string;
  name: string;
  /** Tab-scoped variables, on top of the dashboard's. Reserved. */
  variables: DashboardVariable[];
  rows: DashboardRow[];
  tiles: DashboardTile[];
}

/**
 * Per-dashboard preferences, set from the gear in the toolbar. Part of the
 * document rather than per browser, so everyone who opens a dashboard sees it
 * behave the same way - which matters once dashboards are shared.
 */
export interface DashboardSettings {
  /** Hovering one chart marks the same X on every chart sharing its X axis. */
  sharedCrosshair: boolean;
  /**
   * With the shared crosshair, every chart it reaches also shows its tooltip
   * at that point, so hovering one chart reads out all of them. Nothing
   * without the crosshair.
   */
  sharedTooltip: boolean;
  /**
   * Tiles rise into free space above them after a move, as in Grafana. Off,
   * a tile stays where it is dropped and gaps are allowed.
   */
  floatTiles: boolean;
  /**
   * Each tile's title strip. Off, the charts fill their tiles and a tile's
   * controls appear over its corner on hover; Edit mode always shows the strip,
   * because it is the drag handle.
   */
  showTitles: boolean;
  /**
   * Each tile's card - background and outline. Off, charts sit straight on the
   * page background, for a dashboard that reads as one surface.
   */
  tileFrames: boolean;
  /**
   * Filter while dragging: a range filter narrows the charts as its handles
   * move and a list applies each tick at once, instead of on release / Apply.
   * Off by default - every step refetches every chart on the tab.
   */
  liveSlicers: boolean;
  /**
   * How often Live tiles move on: `cadence` asks each tile's tables how often
   * they are written (the Live API's `poll_seconds`), a number is seconds, and
   * `off` draws them once like any other tile. EOS tiles never refresh - their
   * data changes daily, and a refetch costs the one shared DuckDB.
   */
  liveRefresh: LiveRefresh;
}

/** The choices the toolbar's Live menu offers, in seconds where a number. */
export type LiveRefresh = 'off' | 'cadence' | 10 | 30 | 60 | 300;
export const LIVE_REFRESH_CHOICES: readonly LiveRefresh[] = ['off', 'cadence', 10, 30, 60, 300];

export const DEFAULT_SETTINGS: Readonly<DashboardSettings> = {
  sharedCrosshair: true,
  sharedTooltip: true,
  floatTiles: true,
  showTitles: true,
  tileFrames: true,
  liveSlicers: false,
  liveRefresh: 'cadence',
};

export interface Dashboard {
  id: string;
  name: string;
  /** Named values tiles reference - which ship, which window. See `variables.ts`. */
  variables: DashboardVariable[];
  tabs: DashboardTab[];
  settings: DashboardSettings;
  /** Dashboard-wide filter rows, applied under every tile's own. Reserved. */
  filters: unknown[];
  /** Owning folder, or null for the root level. Folders do not nest. */
  folderId: string | null;
  schemaVersion: number;
  updatedAt: string;
  /**
   * The server's row version, for optimistic concurrency. Absent on a document
   * that has never been saved to a server - which is every one of them while
   * the repository is `localStorage`.
   */
  version?: number;
  /**
   * True for a row from the browse list that has not had its document fetched
   * yet: `tabs` is empty because nobody asked, not because it is empty.
   * Opening it hydrates it, and nothing may save a partial document over a
   * real one.
   */
  partial?: boolean;
  /**
   * What the server counted in the stored document, for a partial row to show
   * without fetching it. Once `tabs` is real, `tabs` is the answer instead.
   */
  counts?: { tiles: number; tabs: number };
  /**
   * How this viewer reached the dashboard, as the server worked it out per row.
   *
   * Absent on a browser-stored dashboard, where there is one person and they
   * own everything - so everything that reads this treats "absent" as full
   * access, and the no-database app stays exactly the app it was.
   */
  access?: DashboardAccess;
}

export interface DashboardAccess {
  isOwner: boolean;
  /** Owner, editor, or root. A viewer gets the dashboard read-only. */
  canEdit: boolean;
  /** Handed to this viewer by name or through a group. */
  sharedDirectly: boolean;
  /** People and groups it is shared with; null unless this viewer may edit it. */
  shareCount: number | null;
  ownerName: string | null;
  ownerEmail: string | null;
}

/**
 * Whether this viewer may change the dashboard.
 *
 * The server decides, and says so on every row; this is only the reading of it.
 * Absent means the local store, where there is nobody to share with. Every
 * write is checked again on the server - hiding a control is politeness, not
 * protection.
 */
export function canEditDashboard(d: Dashboard | null | undefined): boolean {
  return !!d && d.access?.canEdit !== false;
}

/**
 * A folder of dashboards.
 *
 * Flat by design - a folder holds dashboards, never other folders. That is what
 * Grafana settled on, and nesting turns "where did I put it?" into a search
 * problem the browse list already solves.
 */
export interface DashboardFolder {
  id: string;
  name: string;
  createdAt: string;
}

/** Everything the browse page needs, as one document. */
export interface DashboardStore {
  folders: DashboardFolder[];
  dashboards: Dashboard[];
}

/**
 * Where dashboards live. The localStorage implementation below is the only one
 * today; an API-backed one lands with identity (`docs/plans/dashboards.md`).
 */
export interface DashboardRepository {
  /**
   * The browse list. May return **partial** dashboards - the row without its
   * tabs - which is what the server does, because reading every document to
   * draw a list of names is the one thing that makes this expensive.
   */
  load(): Promise<DashboardStore>;
  /** One whole document, fetched when it is opened. */
  loadDashboard(id: string): Promise<Dashboard | null>;
  /**
   * Write the document. `force` drops the optimistic-concurrency check, and is
   * only ever passed after somebody has been shown that they lost a race and
   * chosen to overwrite anyway.
   */
  saveDashboard(dashboard: Dashboard, options?: { force?: boolean }): Promise<void>;
  removeDashboard(id: string): Promise<void>;
  /**
   * Write a folder. `create` says this one has never been stored, which the
   * server needs in order to choose between POST and PUT - it cannot be
   * inferred from an id the client made up.
   */
  saveFolder(folder: DashboardFolder, options?: { create?: boolean }): Promise<void>;
  /** Removes the folder; its dashboards fall back to the root level. */
  removeFolder(id: string): Promise<void>;
}

/* ── ids ────────────────────────────────────────────────── */

export function genId(prefix: string): string {
  return prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

/* ── localStorage repository ────────────────────────────── */

/**
 * Coerce whatever came out of storage into a valid dashboard, dropping anything
 * unrecognisable rather than letting a half-parsed document reach the renderer.
 * Also the seam a future `schemaVersion` migration hooks into.
 */
function migrate(raw: unknown): Dashboard | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Partial<Dashboard> & { schemaVersion?: number };
  if (typeof d.id !== 'string' || typeof d.name !== 'string') return null;
  const tabs = Array.isArray(d.tabs) ? d.tabs : [];
  return {
    id: d.id,
    name: d.name,
    folderId: typeof d.folderId === 'string' ? d.folderId : null,
    variables: normalizeVariables(d.variables),
    settings: normalizeSettings(d.settings),
    filters: Array.isArray(d.filters) ? d.filters : [],
    schemaVersion: SCHEMA_VERSION,
    updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : new Date().toISOString(),
    ...(typeof d.version === 'number' ? { version: d.version } : {}),
    tabs: tabs
      .filter((t): t is DashboardTab => !!t && typeof t.id === 'string')
      .map((t) => ({
        id: t.id,
        name: typeof t.name === 'string' ? t.name : 'Tab',
        variables: Array.isArray(t.variables) ? normalizeVariables(t.variables, false) : [],
        rows: (Array.isArray(t.rows) ? t.rows : [])
          .filter((r): r is DashboardRow => !!r && typeof r.id === 'string')
          .map((r) => ({
            id: r.id,
            title: typeof r.title === 'string' ? r.title : '',
            collapsed: !!r.collapsed,
            repeat: typeof r.repeat === 'string' ? r.repeat : null,
          })),
        tiles: (Array.isArray(t.tiles) ? t.tiles : [])
          .filter((tile): tile is DashboardTile => !!tile && typeof tile.id === 'string')
          .map(migrateTile),
      })),
  };
}

/**
 * One stored tile, in whatever shape it was written, as a v3 tile.
 *
 * The v2 form is the one that matters: `bindContext` plus a `context` object of
 * raw query params. `bindContext: false` meant "follow the toolbar", which is
 * exactly a reference to the two built-in variables; `true` meant "these ships,
 * this window", which is a literal. Nothing is lost in either direction, which
 * is what lets the lift happen on read rather than in a batch.
 */
function isSlicerDef(v: unknown): v is SlicerDef {
  const s = v as SlicerDef | undefined;
  return (
    !!s &&
    typeof s.column === 'string' &&
    !!s.column &&
    ['text', 'int', 'float', 'time'].includes(s.family) &&
    ['list', 'dropdown', 'range', 'time'].includes(s.mode)
  );
}

/** The tab's charts - what a viewer counts, without the slicers beside them. */
export function chartTiles(tab: Pick<DashboardTab, 'tiles'>): DashboardTile[] {
  return tab.tiles.filter((t) => t.kind !== 'slicer');
}

function migrateTile(raw: DashboardTile & { bindContext?: boolean; context?: ViewContext | null }): DashboardTile {
  return {
    id: raw.id,
    rowId: typeof raw.rowId === 'string' ? raw.rowId : null,
    kind: raw.kind === 'slicer' && isSlicerDef(raw.slicer) ? 'slicer' : 'chart',
    title: typeof raw.title === 'string' ? raw.title : 'Chart',
    query: typeof raw.query === 'string' ? raw.query : '',
    source: typeof raw.source === 'string' && raw.source ? raw.source : DEFAULT_TILE_SOURCE,
    bind: normalizeBind(raw),
    filters: Array.isArray(raw.filters) ? raw.filters : [],
    repeat: typeof raw.repeat === 'string' ? raw.repeat : null,
    chartId: typeof raw.chartId === 'string' ? raw.chartId : null,
    ...(raw.ignoreSlicers === true ? { ignoreSlicers: true } : {}),
    ...(isSlicerDef(raw.slicer) ? { slicer: { ...raw.slicer } } : {}),
    pos: normalizePos(raw.pos, raw.kind === 'slicer' ? MIN_SLICER_H : MIN_TILE_H),
  };
}

/** The v2 `bindContext` + `context` pair, or a v3 `bind`, as a v3 `bind`. */
function normalizeBind(raw: DashboardTile & { bindContext?: boolean; context?: ViewContext | null }): TileBind {
  const stored = raw.bind;
  if (stored && typeof stored === 'object') {
    return {
      ship: isRef(stored.ship) ? stored.ship : shipList(stored.ship),
      window: isRef(stored.window) ? stored.window : normalizeWindow(stored.window),
      ...(stored.extra && typeof stored.extra === 'object' ? { extra: { ...stored.extra } } : {}),
    };
  }
  if (!raw.bindContext) return followsVariables();
  return bindFromContext(raw.context ?? {});
}

/** Whether this tile pins its own ship and window rather than following the dashboard. */
export function tilePinsContext(tile: DashboardTile): boolean {
  return !isRef(tile.bind.ship) || !isRef(tile.bind.window);
}

export interface ContextPins { ship: boolean; time: boolean }

export function bindWithPins(ctx: ViewContext, pins: ContextPins): TileBind {
  const pinned = bindFromContext(ctx);
  return {
    ship: pins.ship ? pinned.ship : `$${SHIP_VAR}`,
    window: pins.time ? pinned.window : `$${WINDOW_VAR}`,
    ...(pins.time && pinned.extra ? { extra: pinned.extra } : {}),
  };
}

/** The ordinary binding: both halves follow the dashboard's variables. */
export function followsVariables(): TileBind {
  return { ship: `$${SHIP_VAR}`, window: `$${WINDOW_VAR}` };
}

/** A pinned binding built from context params - the v2 `context` object. */
export function bindFromContext(ctx: ViewContext): TileBind {
  const ships = [ctx.ship, ...(ctx.ships ?? '').split(',')].filter(
    (s): s is string => !!s,
  );
  const bind: TileBind = {
    ship: [...new Set(ships)],
    window: normalizeWindow({ preset: ctx.preset, start: ctx.start, end: ctx.end }),
  };
  const extra: Record<string, string> = {};
  if (ctx.pc) extra.pc = ctx.pc;
  if (ctx.live) extra.live = ctx.live;
  if (Object.keys(extra).length) bind.extra = extra;
  return bind;
}

function shipList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((s): s is string => typeof s === 'string' && !!s) : [];
}

function normalizeWindow(value: unknown): TimeWindow {
  if (!value || typeof value !== 'object') return {};
  const w = value as TimeWindow;
  const out: TimeWindow = {};
  if (typeof w.preset === 'string' && w.preset) out.preset = w.preset;
  if (typeof w.start === 'string' && w.start) out.start = w.start;
  if (typeof w.end === 'string' && w.end) out.end = w.end;
  return out;
}

/**
 * Stored variables over the built-ins.
 *
 * A document that predates variables gets the two defaults, which describe what
 * it already did. `withDefaults` is false for tab-scoped lists, where an empty
 * list means "nothing extra here" rather than "not written yet".
 */
export function normalizeVariables(raw: unknown, withDefaults = true): DashboardVariable[] {
  const stored = Array.isArray(raw)
    ? raw.filter((v): v is DashboardVariable => !!v && typeof (v as DashboardVariable).id === 'string')
    : [];
  if (!withDefaults) return stored.map(normalizeVariable);
  const out = stored.map(normalizeVariable);
  for (const fallback of defaultVariables()) {
    if (!out.some((v) => v.id === fallback.id)) out.push(fallback);
  }
  return out;
}

function normalizeVariable(v: DashboardVariable): DashboardVariable {
  // Ship and window no longer follow Explore: a dashboard saved "attached"
  // (`url`) holds its own from now on - its value, if it stored one, or the
  // selection it is first opened with (`adoptExploreSelection`).
  const builtIn = v.id === SHIP_VAR || v.id === WINDOW_VAR;
  const binding =
    v.binding === 'fixed' || v.binding === 'lastUsed' ? v.binding : builtIn ? 'fixed' : 'url';
  const type =
    v.type === 'ship' || v.type === 'timeRange' || v.type === 'query' || v.type === 'custom'
      ? v.type
      : 'custom';
  return {
    ...v,
    type,
    binding,
    label: typeof v.label === 'string' && v.label ? v.label : v.id,
    default: v.default ?? null,
  };
}

/** Stored settings over the defaults, keeping only keys of the right type. */
export function normalizeSettings(raw: unknown): DashboardSettings {
  const out: DashboardSettings = { ...DEFAULT_SETTINGS };
  if (raw && typeof raw === 'object') {
    for (const key of Object.keys(DEFAULT_SETTINGS) as Array<keyof DashboardSettings>) {
      const v = (raw as Record<string, unknown>)[key];
      if (key === 'liveRefresh') {
        if (LIVE_REFRESH_CHOICES.includes(v as LiveRefresh)) out.liveRefresh = v as LiveRefresh;
      } else if (typeof v === 'boolean') {
        out[key] = v;
      }
    }
  }
  return out;
}

/** Clamp a stored position into the grid, so a bad write cannot break layout. */
function normalizePos(pos: Partial<TilePos> | undefined, minH = MIN_TILE_H): TilePos {
  const w = clamp(Math.round(pos?.w ?? DEFAULT_TILE_W), MIN_TILE_W, GRID_COLS);
  return {
    w,
    h: Math.max(minH, Math.round(pos?.h ?? DEFAULT_TILE_H)),
    x: clamp(Math.round(pos?.x ?? 0), 0, GRID_COLS - w),
    y: Math.max(0, Math.round(pos?.y ?? 0)),
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, Number.isFinite(v) ? v : lo));
}

function readStore(): DashboardStore {
  try {
    const raw = localStorage.getItem(DASHBOARDS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        folders: Array.isArray(parsed?.folders)
          ? parsed.folders.filter(isFolder).map((f: DashboardFolder) => ({ ...f }))
          : [],
        dashboards: Array.isArray(parsed?.dashboards)
          ? parsed.dashboards.map(migrate).filter((d: Dashboard | null): d is Dashboard => d !== null)
          : [],
      };
    }
    // v1 was a bare array of dashboards with no folders. Read it once and write
    // it forward, so an existing dashboard survives the schema change.
    const legacy = localStorage.getItem(DASHBOARDS_KEY_V1);
    if (legacy) {
      const arr = JSON.parse(legacy);
      if (Array.isArray(arr)) {
        const store: DashboardStore = {
          folders: [],
          dashboards: arr.map(migrate).filter((d): d is Dashboard => d !== null),
        };
        writeStore(store);
        return store;
      }
    }
  } catch (e) {
    console.warn('Failed to read dashboards', e);
  }
  return { folders: [], dashboards: [] };
}

function isFolder(f: unknown): f is DashboardFolder {
  return !!f && typeof (f as DashboardFolder).id === 'string' && typeof (f as DashboardFolder).name === 'string';
}

function writeStore(store: DashboardStore): void {
  try {
    localStorage.setItem(DASHBOARDS_KEY, JSON.stringify(store));
  } catch (e) {
    console.warn('Failed to persist dashboards', e);
  }
}

export const localDashboardRepository: DashboardRepository = {
  async load(): Promise<DashboardStore> {
    return readStore();
  },

  /** Whole documents were already in the list; this is the interface, not a fetch. */
  async loadDashboard(id: string): Promise<Dashboard | null> {
    return readStore().dashboards.find((d) => d.id === id) ?? null;
  },

  async saveDashboard(dashboard: Dashboard): Promise<void> {
    const store = readStore();
    const idx = store.dashboards.findIndex((d) => d.id === dashboard.id);
    if (idx >= 0) store.dashboards[idx] = dashboard;
    else store.dashboards.push(dashboard);
    writeStore(store);
  },

  async removeDashboard(id: string): Promise<void> {
    const store = readStore();
    store.dashboards = store.dashboards.filter((d) => d.id !== id);
    writeStore(store);
  },

  async saveFolder(folder: DashboardFolder): Promise<void> {
    const store = readStore();
    const idx = store.folders.findIndex((f) => f.id === folder.id);
    if (idx >= 0) store.folders[idx] = folder;
    else store.folders.push(folder);
    writeStore(store);
  },

  async removeFolder(id: string): Promise<void> {
    const store = readStore();
    store.folders = store.folders.filter((f) => f.id !== id);
    // Its dashboards are kept and fall back to the root, rather than being
    // deleted with the folder - losing a dashboard to a mis-clicked folder
    // delete is not a recoverable mistake here, with no server-side history.
    store.dashboards = store.dashboards.map((d) =>
      d.folderId === id ? { ...d, folderId: null } : d,
    );
    writeStore(store);
  },
};

/** Swappable so tests - and later the API implementation - can stand in. */
let repository: DashboardRepository = localDashboardRepository;

export function setDashboardRepository(repo: DashboardRepository): void {
  repository = repo;
}

/* ── Atoms ──────────────────────────────────────────────── */

export const dashboards = new Atom<Dashboard[]>([]);
export const folders = new Atom<DashboardFolder[]>([]);
export const activeDashboardId = new Atom<string | null>(null);
export const activeDashboardTabId = new Atom<string | null>(null);
export const dashboardsLoaded = new Atom<boolean>(false);

/**
 * Which of the section's two pages is showing: the browse list, or one open
 * dashboard. Opening a dashboard is what moves between them, and the `db=`
 * URL param carries it, so a dashboard is linkable.
 */
export type DashboardRoute = 'browse' | 'view';
export const dashboardRoute = new Atom<DashboardRoute>('browse');

/** Browse-page UI: the search box, and which folders are open. */
export const browseQuery = new Atom<string>('');
export const expandedFolders = new Atom<Set<string>>(new Set());
/** The built-in "Shared with me" folder starts closed: it is not one you filed. */
export const sharedFolderOpen = new Atom<boolean>(false);

/** Someone else's dashboard that reached this viewer through a share. */
export function isSharedWithMe(d: Dashboard): boolean {
  return !!d.access && !d.access.isOwner;
}

/**
 * Edit mode gates dragging, resizing and the destructive controls.
 *
 * Locked by default: a dashboard is read far more often than it is arranged, and
 * an always-live drag surface means every attempt to hover a chart is one slip
 * from silently rearranging someone's layout.
 */
export const dashboardEditing = new Atom<boolean>(false);

/**
 * Unsaved edits on the open dashboard.
 *
 * Saving is explicit (see the module note), so this is what the toolbar's Save
 * button lights up from, what the leave guards ask about, and what a discard
 * clears. It is per-session: a reload drops unsaved work, which is the honest
 * consequence of not writing on every drag and is why the guards exist.
 */
export const dashboardDirty = new Atom<boolean>(false);

/** True while an opened dashboard's document is still being fetched. */
export const dashboardHydrating = new Atom<boolean>(false);

export function activeDashboard(): Dashboard | null {
  const id = activeDashboardId.value;
  return dashboards.value.find((d) => d.id === id) ?? null;
}

export function activeTab(): DashboardTab | null {
  const d = activeDashboard();
  if (!d) return null;
  const id = activeDashboardTabId.value;
  return d.tabs.find((t) => t.id === id) ?? d.tabs[0] ?? null;
}

/* ── Load ───────────────────────────────────────────────── */

/**
 * Read the store. Safe to call more than once, and deliberately does **not**
 * auto-select a dashboard: the section opens on the browse list, the way
 * Grafana's does.
 */
export async function loadDashboards(): Promise<void> {
  const store = await repository.load();
  dashboards.set(store.dashboards);
  folders.set(store.folders);
  // Folders start closed. A list of folder names is the shape of the section -
  // you open the one you want - and a dozen folders opened at once is a page of
  // dashboards with the folders lost among them.
  expandedFolders.set(new Set());
  dashboardsLoaded.set(true);
}

/**
 * Open a dashboard: select it, switch to the view page, and fetch its document
 * if the list only carried the row.
 *
 * The page turns immediately and the tiles arrive when the document does -
 * `dashboardHydrating` is what the view shows in between. Waiting for the fetch
 * before switching would make every open feel like a page load, which is the
 * thing splitting list from document was meant to avoid.
 */
export function openDashboard(id: string): void {
  const d = dashboards.value.find((x) => x.id === id);
  if (!d) return;
  activeDashboardId.set(id);
  activeDashboardTabId.set(d.tabs[0]?.id ?? null);
  dashboardRoute.set('view');
  dashboardDirty.set(false);
  if (d.partial) void hydrate(id);
}

/** Fetch one document into the list, replacing the row the list carried. */
async function hydrate(id: string): Promise<void> {
  dashboardHydrating.set(true);
  try {
    const full = await repository.loadDashboard(id);
    if (!full) return;
    dashboards.set(dashboards.value.map((d) => (d.id === id ? { ...full, partial: false } : d)));
    if (activeDashboardId.value === id) {
      activeDashboardTabId.set(full.tabs[0]?.id ?? null);
    }
  } catch (e) {
    console.warn('Failed to open the dashboard', e);
  } finally {
    dashboardHydrating.set(false);
  }
}

/**
 * Re-read one row's access after a share changed, and nothing else.
 *
 * Only `access` is taken from the answer. The document in memory may hold
 * unsaved edits, and a badge is never worth overwriting somebody's arrangement
 * with the stored version - which is what merging the whole row would do.
 */
export async function refreshDashboardRow(id: string): Promise<void> {
  try {
    const fresh = await repository.loadDashboard(id);
    const row = dashboards.value.find((d) => d.id === id);
    if (!fresh?.access || !row) return;
    row.access = fresh.access;
    dashboards.notify();
  } catch {
    // The badge is cosmetic; the share itself already succeeded or already said
    // that it did not.
  }
}

/** Back to the list. The selection is kept so the row can stay highlighted. */
export function closeDashboard(): void {
  dashboardRoute.set('browse');
}

export function toggleFolder(id: string): void {
  const next = new Set(expandedFolders.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedFolders.set(next);
}

export function selectTab(tabId: string): void {
  activeDashboardTabId.set(tabId);
}

/* ── Mutation ───────────────────────────────────────────── */

/**
 * Apply `mutate` to the active dashboard, then notify and persist.
 *
 * Every mutation funnels through here so `updatedAt`, the atom notify and the
 * write cannot drift apart - and so persistence stays one line to redirect at a
 * server later.
 */
function editActive(mutate: (d: Dashboard) => void): void {
  const d = activeDashboard();
  if (!d) return;
  mutate(d);
  d.updatedAt = new Date().toISOString();
  dashboards.notify();
  dashboardDirty.set(true);
}

/**
 * The same funnel, for a change made from outside this module - the Assistant's
 * edits. They are an arranging session like any other: unsaved until Save.
 */
export function editActiveDashboard(mutate: (d: Dashboard) => void): void {
  editActive(mutate);
}

/**
 * Wait for the open dashboard's document, when the list only carried its row.
 * Resolves false if it never arrives (the fetch failed or took too long).
 */
export async function whenHydrated(id: string, timeoutMs = 15_000): Promise<boolean> {
  const started = Date.now();
  for (;;) {
    const d = dashboards.value.find((x) => x.id === id);
    if (d && !d.partial) return true;
    if (!d || Date.now() - started > timeoutMs) return false;
    if (!dashboardHydrating.value && d.partial) {
      // Nothing is fetching it - `openDashboard` was not the way it got selected.
      void hydrate(id);
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

/**
 * Write the active dashboard, and say whether it worked.
 *
 * This is the only path from an arranged layout to storage. A failed save is
 * reported, never swallowed: the opposite rule to `sp_corrections.py`, where an
 * outage must not break predictions - here the whole point is that the user
 * knows whether their arrangement survived.
 */
export async function saveActiveDashboard(options: { force?: boolean } = {}): Promise<
  true | Error
> {
  const d = activeDashboard();
  if (!d) return new Error('No dashboard is open.');
  // A row whose document never arrived would save an empty dashboard over a
  // real one. There is nothing to write until it is hydrated.
  if (d.partial) return new Error('This dashboard has not finished loading.');
  try {
    // `force` is the "overwrite theirs" answer to a conflict: the same row, the
    // same id, written without the version check. It must never become a
    // create, or the answer to a conflict would be a second dashboard.
    await repository.saveDashboard(d, options.force ? { force: true } : undefined);
    dashboardDirty.set(false);
    return true;
  } catch (e) {
    console.warn('Failed to save dashboard', e);
    return e instanceof Error ? e : new Error(String(e));
  }
}

/**
 * Throw away unsaved edits by re-reading the stored document.
 *
 * Re-reads the whole store rather than the one dashboard: a discard that left
 * the list holding a half-edited object would be a worse state than the one it
 * was undoing.
 */
export async function discardActiveChanges(): Promise<void> {
  const id = activeDashboardId.value;
  await loadDashboards();
  dashboardDirty.set(false);
  if (id && !dashboards.value.some((d) => d.id === id)) {
    // The dashboard was never stored at all - a create that was discarded.
    activeDashboardId.set(null);
    dashboardRoute.set('browse');
  }
}

/**
 * Create a dashboard and wait for storage to accept it.
 *
 * Async because the server, not this module, decides a dashboard's id: opening
 * one before the write came back would select an id that is about to change,
 * and the dashboard would vanish from under the person who just made it. The
 * row appears in the list immediately either way - what is awaited is only the
 * write - and a failed create takes it back out rather than leaving a phantom.
 */
export async function createDashboard(
  name: string,
  folderId: string | null = null,
): Promise<Dashboard | null> {
  const tab: DashboardTab = { id: genId('t'), name: 'Overview', variables: [], rows: [], tiles: [] };
  const dashboard: Dashboard = {
    id: genId('d'),
    name: name.trim() || 'New dashboard',
    // A new dashboard owns the selection visible at creation time. Explore and
    // the dashboard may then change independently; the toolbar switch can still
    // attach it again explicitly.
    variables: defaultDetachedVariables(
      plottedShipIds(),
      activePreset.value && activePreset.value !== 'custom'
        ? { preset: activePreset.value }
        : { start: startDate.value, end: endDate.value },
    ),
    tabs: [tab],
    filters: [],
    folderId,
    settings: { ...DEFAULT_SETTINGS },
    schemaVersion: SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
  };
  dashboards.set([...dashboards.value, dashboard]);
  try {
    await repository.saveDashboard(dashboard);
  } catch (e) {
    console.warn('Failed to create dashboard', e);
    dashboards.set(dashboards.value.filter((d) => d !== dashboard));
    return null;
  }
  // The repository may have given it the server's id; notify so anything
  // holding the list re-reads it.
  dashboards.notify();
  return dashboard;
}

/* ── Folders ────────────────────────────────────────────── */

export async function createFolder(name: string): Promise<DashboardFolder | null> {
  const folder: DashboardFolder = {
    id: genId('f'),
    name: name.trim() || 'New folder',
    createdAt: new Date().toISOString(),
  };
  folders.set([...folders.value, folder]);
  try {
    await repository.saveFolder(folder, { create: true });
  } catch (e) {
    console.warn('Failed to create folder', e);
    folders.set(folders.value.filter((f) => f !== folder));
    return null;
  }
  expandedFolders.set(new Set([...expandedFolders.value, folder.id]));
  folders.notify();
  return folder;
}

export async function renameFolder(id: string, name: string): Promise<true | Error> {
  const folder = folders.value.find((f) => f.id === id);
  if (!folder) return new Error('That folder is no longer here.');
  const before = folder.name;
  folder.name = name.trim() || folder.name;
  folders.notify();
  try {
    await repository.saveFolder(folder);
    return true;
  } catch (e) {
    folder.name = before;
    folders.notify();
    console.warn('Failed to rename folder', e);
    return e instanceof Error ? e : new Error(String(e));
  }
}

/** Delete a folder. Its dashboards move to the root rather than going with it. */
export async function deleteFolder(id: string): Promise<true | Error> {
  const index = folders.value.findIndex((f) => f.id === id);
  if (index < 0) return true;
  const removed = folders.value[index]!;
  const held = dashboards.value.filter((d) => d.folderId === id).map((d) => d.id);

  folders.set(folders.value.filter((f) => f.id !== id));
  dashboards.set(dashboards.value.map((d) => (d.folderId === id ? { ...d, folderId: null } : d)));

  try {
    await repository.removeFolder(id);
    return true;
  } catch (e) {
    const restored = [...folders.value];
    restored.splice(index, 0, removed);
    folders.set(restored);
    dashboards.set(
      dashboards.value.map((d) => (held.includes(d.id) ? { ...d, folderId: id } : d)),
    );
    console.warn('Failed to delete folder', e);
    return e instanceof Error ? e : new Error(String(e));
  }
}

/**
 * Apply a change to one dashboard, write it, and put it back if the write fails.
 *
 * List-level actions - rename, move, delete - are not part of an arranging
 * session, so they still write immediately rather than waiting for Save. What
 * they must not do is *look* like they worked: against `localStorage` a write
 * could not fail, against the API it can be refused, conflict, or 503, and a
 * row that keeps its new name on screen after the server said no is a lie.
 *
 * The write goes through the repository, so it carries the `version` the
 * document was read at like every other write - two people renaming the same
 * dashboard race at the write rather than silently.
 */
async function writeDashboard(id: string, mutate: (d: Dashboard) => void): Promise<true | Error> {
  const d = dashboards.value.find((x) => x.id === id);
  if (!d) return new Error('That dashboard is no longer here.');
  const before = { name: d.name, folderId: d.folderId, updatedAt: d.updatedAt };
  mutate(d);
  d.updatedAt = new Date().toISOString();
  dashboards.notify();
  try {
    await repository.saveDashboard(d);
    return true;
  } catch (e) {
    Object.assign(d, before);
    dashboards.notify();
    console.warn('Failed to write dashboard', e);
    return e instanceof Error ? e : new Error(String(e));
  }
}

/** Move a dashboard into a folder, or to the root with `null`. */
export function moveDashboard(id: string, folderId: string | null): Promise<true | Error> {
  const d = dashboards.value.find((x) => x.id === id);
  if (!d || d.folderId === folderId) return Promise.resolve(true as const);
  return writeDashboard(id, (dash) => {
    dash.folderId = folderId;
  });
}

export function renameDashboard(id: string, name: string): Promise<true | Error> {
  const trimmed = name.trim();
  if (!trimmed) return Promise.resolve(true as const);
  return writeDashboard(id, (dash) => {
    dash.name = trimmed;
  });
}

export function updateDashboardSettings(patch: Partial<DashboardSettings>): void {
  editActive((d) => {
    d.settings = { ...d.settings, ...patch };
  });
}

/**
 * Delete a dashboard. The row goes at once and comes back if the server refuses.
 *
 * Optimistic because a delete that waits feels broken, reversible because a
 * delete that *appears* to work and did not is the worst of the three states.
 */
export async function deleteDashboard(id: string): Promise<true | Error> {
  const index = dashboards.value.findIndex((d) => d.id === id);
  if (index < 0) return true;
  const removed = dashboards.value[index]!;
  const wasOpen = activeDashboardId.value === id;

  dashboards.set(dashboards.value.filter((d) => d.id !== id));
  if (wasOpen) {
    activeDashboardId.set(null);
    activeDashboardTabId.set(null);
    dashboardRoute.set('browse');
  }

  try {
    await repository.removeDashboard(id);
    return true;
  } catch (e) {
    const restored = [...dashboards.value];
    restored.splice(index, 0, removed);
    dashboards.set(restored);
    console.warn('Failed to delete dashboard', e);
    return e instanceof Error ? e : new Error(String(e));
  }
}

export function addTab(name: string): void {
  editActive((d) => {
    const tab: DashboardTab = {
      id: genId('t'),
      name: name.trim() || `Tab ${d.tabs.length + 1}`,
      variables: [],
      rows: [],
      tiles: [],
    };
    d.tabs.push(tab);
    activeDashboardTabId.set(tab.id);
  });
}

export function renameTab(tabId: string, name: string): void {
  editActive((d) => {
    const tab = d.tabs.find((t) => t.id === tabId);
    if (tab) tab.name = name.trim() || tab.name;
  });
}

export function deleteTab(tabId: string): void {
  editActive((d) => {
    // The last tab stays: a dashboard with no tabs has nowhere to put a tile,
    // and the empty state would read as data loss rather than an empty tab.
    if (d.tabs.length <= 1) return;
    d.tabs = d.tabs.filter((t) => t.id !== tabId);
    if (activeDashboardTabId.value === tabId) activeDashboardTabId.set(d.tabs[0]!.id);
  });
}

export function reorderTabs(fromId: string, toId: string): void {
  editActive((d) => {
    const from = d.tabs.findIndex((t) => t.id === fromId);
    const to = d.tabs.findIndex((t) => t.id === toId);
    if (from < 0 || to < 0 || from === to) return;
    const [moved] = d.tabs.splice(from, 1);
    if (moved) d.tabs.splice(to, 0, moved);
  });
}

/* ── Tiles ──────────────────────────────────────────────── */

/**
 * First position where a `w`x`h` tile fits without overlapping, scanning rows
 * top-down and columns left-to-right.
 *
 * Grafana-style layouts are read top-to-bottom, so a new tile belongs in the
 * first hole rather than always at the bottom - otherwise a dashboard that has
 * had one tile deleted grows a permanent gap.
 */
export function findSlot(tiles: DashboardTile[], w: number, h: number): { x: number; y: number } {
  const width = Math.min(w, GRID_COLS);
  const maxY = tiles.reduce((m, t) => Math.max(m, t.pos.y + t.pos.h), 0);
  for (let y = 0; y <= maxY; y++) {
    for (let x = 0; x + width <= GRID_COLS; x++) {
      if (!tiles.some((t) => overlaps(t.pos, { x, y, w: width, h }))) return { x, y };
    }
  }
  return { x: 0, y: maxY };
}

export function overlaps(a: TilePos, b: TilePos): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * Add a tile to the active tab from a full query string - what the chart
 * builder produces when it is saved.
 *
 * The query arrives complete, context included; `stripContext` keeps the
 * recipe, and `bindWithPins` captures only the selected context dimensions.
 */
export function addTile(title: string, pinContext: boolean | ContextPins, full: string): DashboardTile | null {
  const query = stripContext(full);
  let created: DashboardTile | null = null;
  editActive((d) => {
    const tab = d.tabs.find((t) => t.id === activeDashboardTabId.value) ?? d.tabs[0];
    if (!tab) return;
    const slot = findSlot(tab.tiles, DEFAULT_TILE_W, DEFAULT_TILE_H);
    created = {
      id: genId('c'),
      rowId: null,
      kind: 'chart',
      title: title.trim() || 'Chart',
      query,
      source: sourceOfQuery(full),
      bind: bindWithPins(extractContext(full), typeof pinContext === 'boolean'
        ? { ship: pinContext, time: pinContext } : pinContext),
      filters: [],
      repeat: null,
      chartId: null,
      pos: { ...slot, w: DEFAULT_TILE_W, h: DEFAULT_TILE_H },
    };
    tab.tiles.push(created);
  });
  return created;
}

/**
 * The part of a catalog chart a tile needs. Structural rather than the API
 * type, so this module stays free of the network layer.
 */
export interface LinkableChart {
  id: string;
  title: string;
  /** Full query string - context included when the chart pinned it. */
  query: string;
  bind_context: boolean;
  pin_ship?: boolean;
  pin_time?: boolean;
}

/** A chart's recipe in tile form: the same context split `addTile` makes. */
export function recipeFromChart(
  chart: LinkableChart,
): Pick<DashboardTile, 'title' | 'query' | 'bind' | 'source'> {
  return {
    title: chart.title,
    // The chart's own source - a Live chart stays Live on every dashboard
    // that links it, and follows the catalog if it is ever switched.
    source: sourceOfQuery(chart.query),
    query: stripContext(chart.query),
    // Catalog pins become literals; each unpinned dimension follows its
    // dashboard variable. Older charts without separate flags keep both pins.
    bind: bindWithPins(extractContext(chart.query), {
      ship: chart.pin_ship ?? chart.bind_context,
      time: chart.pin_time ?? chart.bind_context,
    }),
  };
}

/**
 * Add catalog charts to the active tab as linked tiles, each in the first hole
 * that fits - one write for the lot, however many were picked.
 */
export function addLinkedTiles(charts: LinkableChart[]): DashboardTile[] {
  const created: DashboardTile[] = [];
  editActive((d) => {
    const tab = d.tabs.find((t) => t.id === activeDashboardTabId.value) ?? d.tabs[0];
    if (!tab) return;
    for (const chart of charts) {
      const slot = findSlot(tab.tiles, DEFAULT_TILE_W, DEFAULT_TILE_H);
      const tile: DashboardTile = {
        id: genId('c'),
        rowId: null,
        kind: 'chart',
        filters: [],
        repeat: null,
        ...recipeFromChart(chart),
        chartId: chart.id,
        pos: { ...slot, w: DEFAULT_TILE_W, h: DEFAULT_TILE_H },
      };
      tab.tiles.push(tile);
      created.push(tile);
    }
  });
  return created;
}

/**
 * Cut a tile loose from its catalog chart, keeping the recipe on the tile.
 *
 * Pass the chart when it is still reachable, so the copy is of what the tile
 * was actually drawing; without it the tile keeps the recipe it was linked with
 * - the rescue for a chart that has since been deleted or unshared.
 */
export function detachTile(tileId: string, chart?: LinkableChart): void {
  editActive(() => {
    const tile = findTile(tileId);
    if (!tile || !tile.chartId) return;
    if (chart) Object.assign(tile, recipeFromChart(chart));
    tile.chartId = null;
  });
}

export function removeTile(tileId: string): void {
  editActive((d) => {
    for (const tab of d.tabs) tab.tiles = tab.tiles.filter((t) => t.id !== tileId);
  });
}

export function renameTile(tileId: string, title: string): void {
  editActive(() => {
    const tile = findTile(tileId);
    if (tile) tile.title = title.trim() || tile.title;
  });
}

/** Let the tab's slicers narrow this tile again, or stop them. */
export function setTileIgnoresSlicers(tileId: string, ignore: boolean): void {
  editActive(() => {
    const tile = findTile(tileId);
    if (!tile) return;
    if (ignore) tile.ignoreSlicers = true;
    else delete tile.ignoreSlicers;
  });
}

/** How much room a slicer wants, by how it is shown: [columns, rows]. */
export const SLICER_SIZE: Record<SlicerMode, [number, number]> = {
  list: [3, 7],
  dropdown: [4, 2],
  range: [6, 2],
  time: [6, 2],
};

/** Put a slicer on the open tab, in the first free slot. `label` defaults to the column. */
export function addSlicerTile(slicer: SlicerDef, label = ''): DashboardTile | null {
  let created: DashboardTile | null = null;
  editActive((d) => {
    const tab = d.tabs.find((t) => t.id === activeDashboardTabId.value) ?? d.tabs[0];
    if (!tab) return;
    const [w, h] = SLICER_SIZE[slicer.mode];
    const slot = findSlot(tab.tiles, w, h);
    created = {
      id: genId('f'),
      rowId: null,
      kind: 'slicer',
      title: label.trim() || slicer.column,
      query: '',
      source: DEFAULT_TILE_SOURCE,
      bind: followsVariables(),
      filters: [],
      repeat: null,
      chartId: null,
      slicer: { ...slicer },
      pos: { ...slot, w, h },
    };
    tab.tiles.push(created);
  });
  return created;
}

/**
 * Change what a slicer is: its column, how it is shown, its label. A change of
 * shape between a one-row slicer and a list takes that shape's height, since a
 * list squeezed into two rows shows nothing; the width is the person's and stays.
 */
export function updateSlicerTile(tileId: string, slicer: SlicerDef, label = ''): void {
  editActive(() => {
    const tile = findTile(tileId);
    if (!tile?.slicer) return;
    const wasList = tile.slicer.mode === 'list';
    tile.slicer = { ...slicer };
    tile.title = label.trim() || slicer.column;
    if (wasList !== (slicer.mode === 'list')) tile.pos = { ...tile.pos, h: SLICER_SIZE[slicer.mode][1] };
  });
}

export function duplicateTile(tileId: string): void {
  editActive((d) => {
    const tab = d.tabs.find((t) => t.tiles.some((x) => x.id === tileId));
    const tile = tab?.tiles.find((x) => x.id === tileId);
    if (!tab || !tile) return;
    const slot = findSlot(tab.tiles, tile.pos.w, tile.pos.h);
    tab.tiles.push({
      ...tile,
      id: genId('c'),
      title: `${tile.title} copy`,
      pos: { ...slot, w: tile.pos.w, h: tile.pos.h },
    });
  });
}

/** Move or resize a tile. Positions are clamped; overlap is the caller's job. */
export function setTilePos(tileId: string, pos: TilePos): void {
  editActive(() => {
    const tile = findTile(tileId);
    if (tile) tile.pos = normalizePos(pos, minTileH(tile));
  });
}

/**
 * Commit several positions at once - what a drag or resize produces once the
 * collision cascade has run. One write, one save, rather than N of each.
 */
export function setTilePositions(entries: Array<{ id: string; pos: TilePos }>): void {
  editActive(() => {
    for (const entry of entries) {
      const tile = findTile(entry.id);
      if (tile) tile.pos = normalizePos(entry.pos, minTileH(tile));
    }
  });
}

/** Re-point a tile at a different recipe (an edit round-tripped via Explore). */
export function setTileQuery(tileId: string, fullQuery: string, pinContext: boolean | ContextPins): void {
  editActive(() => {
    const tile = findTile(tileId);
    if (!tile) return;
    tile.query = stripContext(fullQuery);
    tile.source = sourceOfQuery(fullQuery);
    tile.bind = bindWithPins(extractContext(fullQuery), typeof pinContext === 'boolean'
      ? { ship: pinContext, time: pinContext } : pinContext);
    // A recipe edited here is this tile's own; left linked, it would never draw.
    tile.chartId = null;
  });
}

function findTile(tileId: string): DashboardTile | null {
  for (const d of dashboards.value) {
    for (const tab of d.tabs) {
      const tile = tab.tiles.find((t) => t.id === tileId);
      if (tile) return tile;
    }
  }
  return null;
}
